(ns frontend.react.virtualized.scrollable-grid.view
  (:require
   [common.localization :as loc]
   [frontend.db :as app-db]
   [frontend.db]
   [frontend.dom-utils :as dom-utils]
   [frontend.logging :as logging]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.react.virtualized.scrollable-grid.content-grid :as content-grid]
   [frontend.react.virtualized.scrollable-grid.events :as events]
   [frontend.react.virtualized.scrollable-grid.header-grid :as header-grid]
   [frontend.react.virtualized.scrollable-grid.header-selection-grid :as header-selection-grid]
   [frontend.react.virtualized.scrollable-grid.selection-grid :as selection-grid]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.modals.events :as modals]
   [frontend.view.modals.notification.view :as notification-modal]
   [reagent.core :as r]
   [ui.dom-globals :as dg]))

(defn column-width
  [avail-width props js-args]
  (let [{:keys [columns select-width]} @props
        {:keys [index]} (js->clj js-args :keywordize-keys true)]
    (if (= index 0)
      select-width
      (events/calculate-current-column-width columns (- index 1) (- avail-width select-width)))))

(defn open-notification!
  [_type title msg lang-kw]
  (modals/open-modal!
   notification-modal/modal-key
   {:title          title
    :body-text      msg
    :ok-button-text (loc/tr :ok nil lang-kw)}))

(defn deselect-hidden-entities!
  "After performing bulk edit, some entities might be filtered out from the view.
   If that happens, this function deselects those hidden entities based on rules discussed with Mikko."
  [{:keys [grid-entity-ids selected deselect-entity!
           all-selection! row-count setup-args]
    :as   args}]
  ;;modal-atom & bulk-edit shouldn't be coupled with 'new-action-view' - fix later:
  (let [modal-atom (get-in args [:new-action-view :modal-atom])
        bulk-edit-modal-open? (and modal-atom
                                   (:bulk-edit-modal @modal-atom))]

    (let [selected-entities (->> (grid/get-selected-items setup-args)
                                 (mapv session/get-entity))]
      ; Force end bulk editing when users loses read and write access to selected entities
      (when (and bulk-edit-modal-open?
                 (seq selected-entities)
                 (every? nil? selected-entities))
        (all-selection!)
        (when modal-atom
          (swap! modal-atom assoc :bulk-edit-modal false))
        (open-notification! :error
                            (loc/tr :attention)
                            (loc/tr :error-no-access-to-selected-entities)
                            :EN)))

    ; When no entities visible, remove all selection
    (when (and (not bulk-edit-modal-open?)
               (= selected :all)
               (= row-count 0))
      (all-selection!))

    ; Bulk edit caused some of the entities to go hidden. Deselect those.
    (when (and (not bulk-edit-modal-open?)
               selected
               (not= selected :all)
               (not= selected {}))
      (doseq [selected-entity-id (keys selected)]
        (when-not (some #{selected-entity-id} grid-entity-ids)
          (if (fn? deselect-entity!)
            (deselect-entity! selected-entity-id)
            (logging/unimplemented "Deselect function not found!")))))))

(defn calc-parent-size
  [dom-elem]
  (when dom-elem
    (let [parent-node (-> dom-elem
                          (dg/parent-node)
                          (dg/parent-node))
          client-width (dg/client-width parent-node)
          client-height (dg/client-height parent-node)
          scroll-width (dg/scroll-width parent-node)
          scroll-height (dg/scroll-height parent-node)]
      [(when (and (number? client-width)
                  (number? scroll-width))
         (min client-width scroll-width))
       (when (and (number? client-height)
                  (number? scroll-height))
         (min client-height scroll-height))])))

(defn render
  [{:keys [hover-atom props-atom grid-state avail-height hover-row hover-col
           col-count row-count resizing-column recompute-grid-sizes!
           ref-atom _header-height editable-grid? on-row-click!]}]
  [:> js/ReactVirtualized.AutoSizer
   (fn [m]
     (let [[parent-width parent-height] (calc-parent-size (:dom-elem @hover-atom))
           header-height (or (:header-height @props-atom) 50)
           browser-scrollbar-width (:scrollbar-width @app-db/browser)
           autosizer-width (or parent-width
                               (aget m "width"))
           autosizer-height (or parent-height
                                (aget m "height"))
           usabled-width (- autosizer-width browser-scrollbar-width 1)
           usable-height (- autosizer-height browser-scrollbar-width 1)
           row-height 40
           ;; action-toolbar requires rewrite - disabling for now:
           new-action-view? false #_(get-in @props-atom [:new-action-view :in-use?])
           selected (:selected @props-atom)
           not-empty-selection? (cond
                                  (keyword? selected) (= selected :all)
                                  (map? selected) (not (empty? selected))
                                  :else false)

           _ (deselect-hidden-entities! (merge @props-atom
                                               {:row-count row-count}))

           new-action-view-visible? (and new-action-view?
                                         not-empty-selection?)
           new-action-view-height (if new-action-view-visible? 50 0)

           content-height (min (- usable-height new-action-view-height)
                               (+ header-height
                                  (* row-height
                                     (if (> row-count 0)
                                       row-count
                                       1000))))]
       (r/as-element
        [:div
         {:ref            (fn [e]
                            (when (and e
                                       (not= e (:dom-elem @hover-atom)))
                              (swap! hover-atom assoc :dom-elem e)))
          :class-name     (fu/cs "zap-grid" (when resizing-column "resizing-column"))
          :style          {:width  (str (+ usabled-width browser-scrollbar-width) "px")             ;; scrollbar width
                           :height (str (+ usable-height browser-scrollbar-width) "px")}            ;; scrollbar height + padding
          :on-mouse-leave (fn [_]
                            (swap! hover-atom assoc :hover {}))}
         (r/as-element
          (into
           [:> js/ReactVirtualized.ScrollSync
            (fn [args]
              (let [;start-time (.getTime (js/Date.))
                    {:keys [onScroll scrollHeight scrollLeft scrollTop scrollWidth]} (js->clj args :keywordize-keys true)
                    had-scroll? (:has-vertical-scroll? @grid-state)
                    _ (when (and (> scrollHeight 80)
                                 (not (= had-scroll? (> scrollHeight avail-height))))
                        (let [old-timeout (:scroll-timeout @grid-state)]
                          (when old-timeout
                            (.clearTimeout js/window old-timeout)
                            (swap! grid-state dissoc :scroll-timeout))
                          (let [new-timeout (.setTimeout js/window
                                                         (fn [_]
                                                           (swap! grid-state dissoc :scroll-timeout)
                                                           (swap! grid-state assoc
                                                                  :has-vertical-scroll?
                                                                  (> scrollHeight avail-height))
                                                           (when recompute-grid-sizes!
                                                             (recompute-grid-sizes!)))
                                                         100)]
                            (swap! grid-state assoc :scroll-timeout new-timeout))))

                    headers-hiccup
                    [header-grid/view
                     (merge @props-atom
                            {:avail-width     (+ usabled-width browser-scrollbar-width)
                             :scroll-height   scrollHeight
                             :scroll-width    scrollWidth
                             :col-count       col-count
                             :hover-atom      hover-atom
                             :hover-row       hover-row
                             :hover-col       hover-col
                             :column-width-fn (partial column-width usabled-width props-atom)
                             :scroll-left     scrollLeft
                             :on-scroll       (fn [_args]
                                                #_(let [top-args (aset args "scrollTop" scrollTop)]
                                                    (onScroll top-args)))})]
                    ;headers-time (.getTime (js/Date.))
                    ;_ (when debug/DEBUG-GRID-SCROLL
                    ;    (logging/warn "Headers " (- headers-time start-time) "ms"))
                    content-hiccup
                    [content-grid/view
                     (merge @props-atom
                            {:avail-width     (+ usabled-width browser-scrollbar-width)             ;; scroll bar width
                             :avail-height    (-> content-height
                                                  (- header-height)
                                                  #_(- new-action-view-height)
                                                  (+ browser-scrollbar-width))                      ;; scroll bar height
                             :scroll-height   scrollHeight
                             :scroll-width    scrollWidth
                             :col-count       col-count
                             :row-count       row-count
                             :editable-grid?  editable-grid?
                             :on-row-click!   on-row-click!
                             :hover-atom      hover-atom
                             :ref-atom        ref-atom
                             :hover-row       hover-row
                             :hover-col       hover-col
                             :row-height      row-height
                             :column-width-fn (partial column-width usabled-width props-atom)
                             :scroll-left     scrollLeft
                             :scroll-top      scrollTop
                             :on-scroll       onScroll})]
                    ;content-time (.getTime (js/Date.))
                    ;_ (when debug/DEBUG-GRID-SCROLL
                    ;    (logging/error "Content " (- content-time headers-time) "ms"))
                    hiccup
                    (r/as-element
                     [:div.zap-grid__scrollable__container
                      (comment
                        [header-selection-grid/view
                         (merge @props-atom
                                {:hover-atom hover-atom}) nil "header-selection-cell"]
                        [selection-grid/view
                         (merge @props-atom
                                {:avail-width    usabled-width
                                 :col-count      col-count
                                 :hover-atom     hover-atom
                                 :hover-row      hover-row
                                 :editable-grid? editable-grid?
                                 :on-row-click!  on-row-click!
                                 :hover-col      hover-col
                                 :scroll-left    scrollLeft
                                 :scroll-top     scrollTop
                                 :on-scroll      onScroll})])
                      [:div.zap-grid__scrollable__grid-container
                       (r/as-element
                        [:div
                         #_(when new-action-view?
                             [new-action-view @props-atom])
                         [:div
                          {:class-name "headers"
                           :style      {:height (str header-height "px")
                                        :width  (str usabled-width "px")}}
                          headers-hiccup]
                         [:div
                          {:style {:height (str content-height "px")
                                   :width  (str usabled-width "px")}}
                          content-hiccup]])]])]
                ;(when debug/DEBUG-GRID-SCROLL
                ;  (logging/log "GRID Hiccup " (- (.getTime (js/Date.)) content-time) "ms"))
                hiccup))]))])))])

(defn view
  [{:keys [props-atom hover-atom]}]
  ;; TODO: remove :hover from grid-state and move to hover-atom
  (let [grid-state (r/atom {:hover {:col nil :row nil}})
        hover-row (r/cursor hover-atom [:hover :row])
        hover-col (r/cursor hover-atom [:hover :col])
        ref-atom (atom {})
        mouse-up-handler (fn [e]
                           (when-not (dom-utils/is-ancestor? (some->> e
                                                                      .-target)
                                                             (:grid-ref @ref-atom))
                             (swap! hover-atom dissoc :active-cell-col :active-cell-row)))
        {:keys [column-widths-state ordered-columns-state
                selected-columns-state]}
        (:setup-args @props-atom)
        recompute-grid-sizes! (:recompute-grid-sizes! @props-atom)
        col-widths-tracker-fn (fn []
                                ;; derefing column-widths-state => this fn will be re-executed
                                ;; (assuming @(r/track col-widths-tracker-fn) somewhere in render-fn)
                                ;; each time column widths change => recompute-grid-sizes!
                                ;;note: :setup-args should be passed inside props-atom
                                ;; for this to work:
                                (when column-widths-state
                                  @column-widths-state)
                                ;;recompute needed also when columns order is updated:
                                (when ordered-columns-state
                                  @ordered-columns-state)
                                ;;and in case there are selected-columns with undefined order:
                                (when selected-columns-state
                                  @selected-columns-state)
                                (recompute-grid-sizes!)
                                ;; always return nil so doesn't accidentally cause additional rerendering:
                                nil)]
    (r/create-class
     {:component-did-mount    (fn [_]
                                (.addEventListener js/document "mouseup" mouse-up-handler))
      :component-will-unmount (fn [_]
                                (.removeEventListener js/document "mouseup" mouse-up-handler))
      :reagent-render
      (fn [args]
        (let [;start-time (.getTime (js/Date.))
              {:keys [props-atom hover-atom]} args
              {:keys [columns header-height avail-height resizing-column row-count recompute-grid-sizes! editable-grid? on-row-click!]} @props-atom
              col-count (or (count columns) 0)
              hover-row @hover-row
              hover-col @hover-col
              ;; derefing to ensure column width changes are reflected in the component:
              _ @(r/track col-widths-tracker-fn)
              ;before-render (.getTime (js/Date.))
              ;_ (when debug/DEBUG-GRID-SCROLL
              ;    (logging/warn "Before render " (- (.getTime (js/Date.)) start-time) "ms"))
              rendered (render {:hover-atom            hover-atom
                                :props-atom            props-atom
                                :grid-state            grid-state
                                :avail-height          avail-height
                                :hover-row             hover-row
                                :ref-atom              ref-atom
                                :hover-col             hover-col
                                :col-count             col-count
                                :row-count             row-count
                                :resizing-column       resizing-column
                                :recompute-grid-sizes! recompute-grid-sizes!
                                :header-height         header-height
                                :editable-grid?        editable-grid?
                                :on-row-click!         on-row-click!})]
          ;(when debug/DEBUG-GRID-SCROLL
          ;  (logging/log "RENDER GRID : " (- (.getTime (js/Date.)) start-time)))
          rendered))})))
